import { isValidJWT } from "./authentication/isValidJWT";
import { describeJwt, embedAuthWarn } from "./embedAuthLog";
import { isEmbedMode } from "./embedMode";
import {
  getPortalOrigin,
  PORTAL_MESSAGES,
  postToPortal,
  SESSION_RECOVER_REASONS,
} from "./embedPortalBridge";

export const TOKEN_RECOVER_THROTTLE_MS = 10000;
export const TOKEN_RECOVER_AUTO_ATTEMPTS = 3;

const TRACKED_TOKENS = 16;

export type ExpiredTokenEvent = { token: string; requested: boolean; exhausted: boolean };
export type TokenRecoverOutcome = "sent" | "throttled" | "exhausted";

type Listener = (event: ExpiredTokenEvent) => void;

const listeners = new Set<Listener>();
const attemptsByToken = new Map<string, number>();
let lastEvent: ExpiredTokenEvent | null = null;
let lastRequestAt = Number.NEGATIVE_INFINITY;
let frameToken: string | null = null;

function post(trigger: string, extra: Record<string, unknown> = {}): boolean {
  lastRequestAt = Date.now();
  const delivered = postToPortal({
    type: PORTAL_MESSAGES.sessionRecover,
    reason: SESSION_RECOVER_REASONS.tokenExpired,
  });
  embedAuthWarn("session.token.recover", { trigger, delivered, ...extra });
  return delivered;
}

// Automatic requests are capped per expired token (a Map, so two dead tokens taking
// turns cannot reset each other's allowance) and share one throttle clock, so a portal
// that keeps answering with a dead token cannot make the frame spin.
function autoRequest(token: string, trigger: string, extra: Record<string, unknown> = {}): TokenRecoverOutcome {
  const used = attemptsByToken.get(token) ?? 0;
  if (used >= TOKEN_RECOVER_AUTO_ATTEMPTS) return "exhausted";
  if (Date.now() - lastRequestAt < TOKEN_RECOVER_THROTTLE_MS) return "throttled";
  attemptsByToken.delete(token);
  attemptsByToken.set(token, used + 1);
  if (attemptsByToken.size > TRACKED_TOKENS) {
    const oldest = attemptsByToken.keys().next().value;
    if (oldest !== undefined) attemptsByToken.delete(oldest);
  }
  post(trigger, { attempt: used + 1, ...extra });
  return "sent";
}

// Called by EmbedSessionNotice with the token the frame currently holds, so a caller
// still carrying a token the frame has since replaced is told apart from a dead session.
export function noteEmbedFrameToken(token: string | null | undefined): void {
  frameToken = token || null;
}

export function reportExpiredEmbedToken(token: string, source: string): void {
  if (!isEmbedMode() || !token) return;
  if (frameToken && frameToken !== token && isValidJWT(frameToken)) {
    embedAuthWarn("session.token.stale-caller", { source, jwt: describeJwt(token) });
    return;
  }
  if (!getPortalOrigin()) {
    embedAuthWarn("session.token.expired.no-portal", { source });
    return;
  }
  const outcome = autoRequest(token, "dropped-call", { source });
  const event: ExpiredTokenEvent = {
    token,
    requested: outcome === "sent",
    exhausted: outcome === "exhausted",
  };
  lastEvent = event;
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch {
    }
  });
}

export function retryExpiredEmbedToken(token: string): TokenRecoverOutcome {
  if (!isEmbedMode() || !token) return "exhausted";
  return autoRequest(token, "notice");
}

export function requestEmbedTokenRecover(): boolean {
  if (!isEmbedMode()) return false;
  return post("manual");
}

// The notice saw a usable token adopted (or a sign-out): nothing is left to replay.
export function settleExpiredEmbedToken(): void {
  lastEvent = null;
}

export function onExpiredEmbedToken(listener: Listener): () => void {
  listeners.add(listener);
  if (lastEvent) listener(lastEvent);
  return () => {
    listeners.delete(listener);
  };
}
